'use client';

import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { monthlyAverages } from '@/data/weather';
import SectionHeading from '@/components/ui/SectionHeading';
import {
  resolveWeatherLabels,
  type WeatherLabels,
} from '@/components/weather/labels';

const monthIndex = [10, 11, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

export default function HistoricalAverages({ labels }: { labels?: WeatherLabels }) {
  const L = resolveWeatherLabels(labels);
  const currentMonth = new Date().getMonth();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <SectionHeading
        title={L.historyTitle}
        subtitle={L.historySubtitle}
        className="mb-8"
      />

      <div
        className={cn(
          'rounded-xl overflow-hidden',
          'bg-white shadow-sm'
        )}
      >
        <div className="overflow-x-auto">
          <table className="w-full font-sans text-sm">
            <thead>
              <tr
                className="bg-[#F0EDE5]"
              >
                {[
                  L.historyHeaders.month,
                  L.historyHeaders.avgTemp,
                  L.historyHeaders.snowDepth,
                  L.historyHeaders.daylight,
                  L.historyHeaders.bestFor,
                ].map(
                  (header) => (
                    <th
                      key={header}
                      className={cn(
                        'px-4 py-3 text-left font-semibold text-xs uppercase tracking-wider',
                        'text-[#9B7B3A]'
                      )}
                    >
                      {header}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {monthlyAverages.map((row, i) => {
                const isCurrentMonth = monthIndex[i] === currentMonth;
                const isEven = i % 2 === 0;

                return (
                  <tr
                    key={row.month}
                    className={cn(
                      'transition-colors',
                      isCurrentMonth
                        ? 'bg-[#9B7B3A]/10 border-l-2 border-l-[#9B7B3A]'
                        : isEven
                          ? 'bg-white'
                          : 'bg-[#FAFAF5]'
                    )}
                  >
                    <td
                      className={cn(
                        'px-4 py-3 font-medium whitespace-nowrap',
                        isCurrentMonth
                          ? 'text-[#9B7B3A] font-bold'
                          : 'text-[#1A1A2E]'
                      )}
                    >
                      {L.historyMonths[row.month] ?? row.month}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 whitespace-nowrap',
                        'text-[#1A1A2E]'
                      )}
                    >
                      {row.avgTemp}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 whitespace-nowrap',
                        'text-[#1A1A2E]'
                      )}
                    >
                      {row.snowDepth}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 whitespace-nowrap',
                        'text-[#1A1A2E]'
                      )}
                    >
                      {L.historyDaylight[row.month] ?? row.daylightHours}
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3',
                        'text-[#5A5A6E]'
                      )}
                    >
                      {L.historyBestFor[row.month] ?? row.bestFor}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
}
